import { SlotKey } from './slot-key';
import { Swap } from '@phosphor-icons/react';
interface AskChipProps {
  label: string;
  /** Its ⌥1–⌥9 number, shown while Option is held. */
  slotKey?: number;
  action: string;
  onClick: () => void;
  /** Its window is shown — the same light grey as an active window tab. */
  active?: boolean;
  /** A third window waits and this chip's window is shown: the click replaces it (swap icon). */
  replacing?: boolean;
}

/** Caps an accessible name at a sane length. `label` can be an approval's
 *  description — for `dispatch_desktop_task` that's the goal itself, up to
 *  20,000 characters (FRONT-2266) — while the visible chip text is already
 *  clipped by the `truncate` class below; a screen reader has no such cap
 *  and would read the whole string. Ellipsis at the end, no export needed
 *  outside this file's own test. */
export function truncateAccessibleName(text: string, max = 120): string {
  return text.length > max ? `${text.slice(0, max - 1)}…` : text;
}

/**
 * The one thing blocking EDDY, spelled out inline (README §3) — rendered
 * only when at least one chat OR Building Session (FRONT-1681) is `asking`.
 * The design's core bet: counts are ambient, but a block is spelled out in
 * words, so this is never reduced to another dot.
 */
export function AskChip({ label, action, onClick, active = false, replacing = false, slotKey }: AskChipProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={truncateAccessibleName(replacing ? `Replace ${label}` : `${action}: ${label}`)}
      // Several chips share the bar now (one per open question), so each one
      // is narrower and may shrink further; the full text is in its name.
      // Never an orange background — the amber dot alone marks a question
      // (eddy, 2026-10-26); hover tint as on the window tabs.
      aria-pressed={active}
      className={`inline-flex h-[38px] max-w-[260px] min-w-[120px] shrink items-center gap-[8px] overflow-hidden rounded-[11px] pr-[12px] pl-[11px] whitespace-nowrap transition-colors hover:bg-[var(--bg-3)]${active ? ' bg-[var(--bg-3)]' : ''}`}
    >
      {replacing ? (
        <Swap size={14} weight="bold" className="shrink-0 text-primary" data-testid="ask-chip-swap-icon" />
      ) : (
        // Static, like the sidebar's dots: no blinking (eddy, 2026-10-26).
        <span className="size-[7px] shrink-0 rounded-full bg-amber-500" />
      )}
      <span className="min-w-0 flex-1 truncate text-left text-[13px] text-[var(--ink-0)]">
        {label}
      </span>
      {/* "Answer" is implied by the chip itself — dropped to save space
          (eddy, 2026-10-26); "Approve" stays, it says something different. */}
      {action !== 'Answer' && (
        <span className="shrink-0 text-[13px] font-medium text-amber-600 dark:text-amber-400">
          {action}
        </span>
      )}
      <SlotKey n={slotKey} />
    </button>
  );
}
